import { cancelDeSoTokenLimitOrder } from "deso-protocol";
import { use, useMemo, useState } from "react";
import { LuTrash2 } from "react-icons/lu";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import { EmptyState } from "@/components/shared/empty-state";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { MaybeUserLabel } from "@/components/shared/maybe-user-label";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableScrollable,
} from "@/components/ui/table";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import { UserContext } from "@/contexts/user-context";
import { FOCUS_TOKEN_DISPLAY_NAME } from "@/utils/constants";
import { focusToUsd } from "@/utils/currency";
import { cn } from "@/utils/lib";
import { guardTxPermission } from "@/utils/spending-limits";

import type { AmmOrderLevel } from "../trade/amm/token-orderbook";

interface CurrentBidsProps {
  orders: AmmOrderLevel[] | null;
  coinUsername: string;
  loading: boolean;
  tokenPublicKey: string;
  totalSupply: number;
  className?: string;
}

export const CurrentBids = ({
  orders,
  loading,
  coinUsername,
  tokenPublicKey,
  totalSupply,
  className = "",
}: CurrentBidsProps) => {
  const { toast } = useToast();
  const { currentUserPublicKey } = use(UserContext);

  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(
    null,
  );
  const { focusExchangeRate, loading: loadingMarketData } =
    useFocusMarketData();

  const [showMyOrdersOnly, setShowMyOrdersOnly] = useState(false);

  const currentUserOrders = useMemo(() => {
    return orders?.filter(
      (order) => order.TransactorPublicKeyBase58Check === currentUserPublicKey,
    );
  }, [currentUserPublicKey, orders]);

  if (loadingMarketData || !focusExchangeRate) {
    return <Skeleton className="h-[200px] w-full" />;
  }

  const totalMarketCap = orders?.[0]
    ? focusToUsd(orders[0].PriceInQuoteCurrency, focusExchangeRate) *
      totalSupply
    : 0;

  const cancelOrder = async (orderId: string) => {
    if (!orderId || !currentUserPublicKey) {
      return;
    }

    setCancellingOrderId(orderId);

    const focusPublicKey = process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY || "";

    try {
      await guardTxPermission({
        GlobalDESOLimit: 1e9,
        DAOCoinOperationLimitMap: {
          [focusPublicKey]: {
            transfer: "UNLIMITED",
          },
        },
        DAOCoinLimitOrderLimitMap: {
          [focusPublicKey]: {
            [tokenPublicKey]: "UNLIMITED",
          },
          [tokenPublicKey]: {
            [focusPublicKey]: "UNLIMITED",
          },
        },
      });
      await cancelDeSoTokenLimitOrder({
        TransactorPublicKeyBase58Check: currentUserPublicKey,
        CancelOrderID: orderId,
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when cancelling your order`,
        details: createToastErrorDetails(e),
      });
    } finally {
      setCancellingOrderId(null);
    }
  };

  const visibleOrders = showMyOrdersOnly ? currentUserOrders : orders;

  return (
    <>
      <div
        className="
          @container mb-1 flex flex-col items-start justify-between gap-1
          lg:flex-row lg:items-center
        "
      >
        <div
          className="
            flex flex-row items-start gap-2
            lg:items-center
          "
        >
          <div
            className="
              mb-2 font-sans text-sm font-semibold text-muted-foreground
              lg:mb-0 lg:text-base
            "
          >
            <span className="@max-[500px]:hidden">Current</span> Bids
          </div>
          <InfoTooltip
            text={
              <span>
                Before the market opens, anyone can place a bid to reserve
                tokens at the lowest possible price, including you! Bids will be
                filled by the Automated Market Maker's sell orders when the
                market opens.
              </span>
            }
          />
        </div>

        <div
          className="
            flex gap-2
            @min-[500px]:gap-4
          "
        >
          <div className="flex items-center space-x-2">
            <Switch
              id="my-bids"
              checked={showMyOrdersOnly}
              onCheckedChange={setShowMyOrdersOnly}
            />
            <Label
              htmlFor="my-bids"
              className="cursor-pointer text-xs text-muted"
            >
              <span className="@max-[500px]:hidden">Show</span> My Bids{" "}
              {(orders || []).length > 0 &&
                `(${currentUserOrders?.length || 0})`}
            </Label>
          </div>

          <div
            className="
            flex items-center gap-1 text-sm font-semibold text-muted
          "
          >
            <span className="@max-[500px]:hidden">Market Cap</span>
            <span
              className="
                hidden
                @max-[500px]:inline
              "
            >
              MC
            </span>
            :
            <div>
              <CoinPriceFormatter
                price={totalMarketCap}
                className="
                  font-shadow-green font-mono text-base font-normal
                  text-emerald-400
                "
              />
            </div>
            <InfoTooltip
              text={
                <span>
                  This is computed as highest bid times total token supply,
                  including all tokens held by the AMM.
                </span>
              }
            />
          </div>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-[400px] w-full" />
      ) : (
        <TableScrollable
          containerClassname={cn(
            `
              scrollable-table relative h-fit max-h-[700px] w-full
              overflow-y-auto rounded-md border
            `,
            className,
          )}
        >
          <TableHeader>
            <TableRow>
              <TableHead
                className="
                  unforce-radius sticky top-0 z-10 bg-background font-sans
                  text-xs
                "
              >
                User
              </TableHead>
              <TableHead
                className="
                  unforce-radius sticky top-0 z-10 bg-background font-sans
                  text-xs
                "
              >
                Amount
              </TableHead>
              <TableHead
                className="
                  unforce-radius sticky top-0 z-10 bg-background font-sans
                  text-xs
                "
              >
                Number of Tokens
              </TableHead>
              <TableHead
                className="
                  unforce-radius sticky top-0 z-10 bg-background font-sans
                  text-xs
                "
              >
                Bid Price
              </TableHead>
              <TableHead
                className="
                  unforce-radius sticky top-0 z-10 bg-background font-sans
                  text-xs
                "
              ></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleOrders && visibleOrders.length > 0 ? (
              visibleOrders.map((level: AmmOrderLevel, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="flex items-center">
                      {level.TransactorPublicKeyBase58Check && (
                        <MaybeUserLabel
                          value={level.TransactorPublicKeyBase58Check}
                          imageSize={32}
                          showDisplayName
                          shortenIfNoProfile
                          displayNameClassName="max-w-[100px] block"
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="p-2">
                    <div className="flex flex-col whitespace-nowrap">
                      <span className="font-mono text-sm text-emerald-400">
                        <CoinPriceFormatter
                          className="text-sm"
                          price={focusToUsd(
                            level.AmountInQuoteCurrency,
                            focusExchangeRate,
                          )}
                        />
                      </span>
                      <span className="font-mono text-xs text-muted">
                        <CoinPriceFormatter
                          isUsd={false}
                          className="font-mono"
                          price={level.AmountInQuoteCurrency}
                        />{" "}
                        {FOCUS_TOKEN_DISPLAY_NAME}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="p-2">
                    <div className="flex flex-col">
                      <span className="font-mono text-sm">
                        <CoinPriceFormatter
                          isUsd={false}
                          className="text-sm"
                          price={
                            level.AmountInQuoteCurrency /
                            level.PriceInQuoteCurrency
                          }
                        />
                      </span>
                      <span className="font-mono text-xs text-muted">
                        {coinUsername}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="p-2">
                    <div className="flex flex-col whitespace-nowrap">
                      <span className="font-mono text-sm text-emerald-400">
                        <CoinPriceFormatter
                          className="text-sm"
                          price={focusToUsd(
                            level.PriceInQuoteCurrency,
                            focusExchangeRate,
                          )}
                        />
                      </span>
                      <span className="font-mono text-xs text-muted">
                        <CoinPriceFormatter
                          isUsd={false}
                          className="font-mono"
                          price={level.PriceInQuoteCurrency}
                        />{" "}
                        {FOCUS_TOKEN_DISPLAY_NAME}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    {currentUserPublicKey &&
                      level.TransactorPublicKeyBase58Check ===
                        currentUserPublicKey && (
                        <Button
                          variant="destructive"
                          size="xs"
                          className="size-6 rounded-full p-0"
                          onClick={() =>
                            level.OrderId && cancelOrder(level.OrderId)
                          }
                          disabled={cancellingOrderId === level?.OrderId}
                        >
                          <LuTrash2 />
                        </Button>
                      )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <>
                <TableRow>
                  <TableCell colSpan={5} className="text-center">
                    <EmptyState
                      title="No bids have been placed yet."
                      description={`Be the first to place a bid on ${coinUsername}`}
                      classNames="py-6"
                    />
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </TableScrollable>
      )}
    </>
  );
};
